import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/PropertyCard";
import SearchFilters from "@/components/SearchFilters";
import { ArrowRight, Award, Users, Home as HomeIcon, Star } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propertiesRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/properties/featured?limit=6`),
        axios.get(`${API}/testimonials`)
      ]);
      setFeaturedProperties(propertiesRes.data);
      setTestimonials(testimonialsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (filters) => {
    const queryParams = new URLSearchParams(filters).toString();
    window.location.href = `/properties?${queryParams}`;
  };

  return (
    <div data-testid="home-page">
      <section 
        className="relative h-[90vh] flex items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1746458258536-b9ee5db20a73?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <p className="text-sm uppercase tracking-widest text-white/90 mb-4 font-semibold">Greater Toronto Area Real Estate</p>
          <h1 className="text-5xl md:text-7xl font-heading font-bold text-white mb-6 leading-tight">
            Find Your Dream Home
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-12 leading-relaxed max-w-2xl mx-auto">
            Luxury properties and exceptional service throughout the GTA
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/properties">
              <Button 
                className="bg-white text-primary hover:bg-white/90 h-14 px-10 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
                data-testid="hero-browse-button"
              >
                Browse Properties
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-primary h-14 px-10 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
                data-testid="hero-contact-button"
              >
                Get In Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SearchFilters onSearch={handleSearch} />
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Featured Listings</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
              Exclusive Properties
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Discover handpicked luxury homes across the Greater Toronto Area
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20" data-testid="loading-indicator">
              <p className="text-muted-foreground">Loading properties...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property) => (
                <PropertyCard key={property.mls_number} property={property} featured />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/properties">
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
                data-testid="view-all-properties-button"
              >
                View All Properties
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">About Paul Murray</p>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
                Your Trusted Real Estate Partner
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                With over 15 years of experience in the Greater Toronto Area real estate market, I've helped hundreds of families find their perfect homes and achieve their real estate goals.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                My commitment to excellence, deep market knowledge, and personalized approach ensure that every client receives the exceptional service they deserve.
              </p>
              <Link to="/about">
                <Button 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
                  data-testid="learn-more-button"
                >
                  Learn More
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1592878995758-02b32ddabdd3?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Paul Murray"
                className="w-full h-[500px] object-cover shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Why Choose Murray Realtor</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
              Excellence in Every Transaction
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <Award className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">15+ Years Experience</h3>
              <p className="text-muted-foreground leading-relaxed">
                Extensive knowledge of the GTA market and proven track record of success
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <Users className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">500+ Happy Clients</h3>
              <p className="text-muted-foreground leading-relaxed">
                Trusted by families throughout Toronto and surrounding areas
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <HomeIcon className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">$200M+ in Sales</h3>
              <p className="text-muted-foreground leading-relaxed">
                Exceptional results and maximum value for every client
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Client Testimonials</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-8 border-l-4 border-accent" data-testid={`testimonial-${testimonial.id}`}>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-semibold text-primary">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/about">
              <Button 
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white h-12 px-8 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
                data-testid="more-testimonials-button"
              >
                More Testimonials
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section 
        className="relative py-20 md:py-32 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1666490971707-d499de4c532b?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-white mb-6">
            Ready to Find Your Dream Home?
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
            Let's start your real estate journey together
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white text-primary hover:bg-white/90 h-14 px-10 rounded-none uppercase tracking-widest text-sm font-semibold transition-all duration-300"
              data-testid="cta-contact-button"
            >
              Contact Me Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;